import getPublicAssetUrl from "utils/PublicAsset";
import onIdle from "utils/OnIdle";

type PreloadManifest = {
    images: string[];
};

const MAX_CONCURRENT_IMAGES = 6;
let navigationAssetsPromise: Promise<void> | null = null;

const preloadImage = (src: string): Promise<void> => new Promise((resolve) => {
    const image = new Image();

    image.decoding = "async";

    image.onload = () => {
        if(typeof image.decode !== "function") {
            resolve();
            return;
        }

        void image.decode().catch(() => undefined).then(() => resolve());
    };

    image.onerror = () => resolve();
    image.src = src;
});

const preloadFonts = async (): Promise<void> => {
    if(!document.fonts) {
        return;
    }

    await document.fonts.ready;

    const fontFaces: FontFace[] = [];

    document.fonts.forEach((fontFace) => fontFaces.push(fontFace));

    await Promise.allSettled(
        fontFaces.map((fontFace) => fontFace.load()),
    );
};

const preloadImages = async (paths: string[]): Promise<void> => {
    let nextImageIndex = 0;

    const preloadNext = async (): Promise<void> => {
        while(nextImageIndex < paths.length) {
            const path = paths[nextImageIndex];

            nextImageIndex += 1;
            await preloadImage(getPublicAssetUrl(path));
        }
    };

    const workersCount = Math.min(MAX_CONCURRENT_IMAGES, paths.length);

    await Promise.all(
        Array.from({ length: workersCount }, () => preloadNext()),
    );
};

const preloadManifestImages = async (): Promise<void> => {
    try {
        const response = await fetch(
            getPublicAssetUrl("preload-assets.json"),
            { cache: "force-cache" },
        );

        if(!response.ok) {
            return;
        }

        const manifest = await response.json() as PreloadManifest;

        if(!Array.isArray(manifest.images)) {
            return;
        }

        const isFlag = (path: string) => path.includes("/flags/");

        await preloadImages(manifest.images.filter(path => !isFlag(path)));
        onIdle(() => void preloadImages(manifest.images.filter(isFlag)));
    } catch(_) {
        // Asset warming must never block or break the application.
    }
};

const preloadNavigationAssets = (): Promise<void> => {
    if(navigationAssetsPromise) {
        return navigationAssetsPromise;
    }

    navigationAssetsPromise = Promise.allSettled([
        preloadManifestImages(),
        preloadFonts(),
    ]).then(() => undefined);

    return navigationAssetsPromise;
};

export default preloadNavigationAssets;
